const REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes

const userTokens = new Map();
const refreshTimers = new Map();

self.addEventListener('install', () => {
  self.skipWaiting();
});

self.addEventListener('activate', (event) => {
  event.waitUntil(self.clients.claim());
});

self.addEventListener('message', async (event) => {
  const { type, payload } = event.data;
  switch (type) {
    case 'START_REFRESH':
      startTokenRefresh(payload);
      break;

    case 'UPDATE_TOKEN':
      updateToken(payload);
      break;

    case 'STOP_REFRESH':
      stopTokenRefresh(payload.sessionId);
      break;

    case 'RECHECK':
      userTokens.forEach((_, id) => scheduleRefresh(id));
      break;
  }
});

/**
 * Initializes token refresh monitoring for a session
 * Prevents duplicate timers for the same session
 */
function startTokenRefresh({ sessionId, token, expiresAt }) {
  const existing = userTokens.get(sessionId);
  if (existing && existing.expiresAt >= expiresAt) return;
  userTokens.set(sessionId, { sessionId, token, expiresAt });
  scheduleRefresh(sessionId);
}

/**
 * Updates token information after successful refresh
 * Reschedule the next refresh based on new expiration
 */
function updateToken({ sessionId, token, expiresAt }) {
  const existing = userTokens.get(sessionId);
  if (existing) {
    userTokens.set(sessionId, { sessionId, token, expiresAt });
    scheduleRefresh(sessionId);
  }
}

/**
 * Schedules token refresh for a specific session
 * Clears any existing timer before setting a new one
 */
function scheduleRefresh(sessionId) {
  const existingTimer = refreshTimers.get(sessionId);
  if (existingTimer) clearTimeout(existingTimer);

  const tokenData = userTokens.get(sessionId);

  if (!tokenData) return;
  if (tokenData.expiresAt - Date.now() <= 0) {
    triggerRefresh(sessionId);
    return;
  }

  const timeUntilRefresh = tokenData.expiresAt - Date.now() - REFRESH_BUFFER;
  if (timeUntilRefresh <= 0) triggerRefresh(sessionId);
  else {
    const timer = setTimeout(() => triggerRefresh(sessionId), timeUntilRefresh);
    refreshTimers.set(sessionId, timer);
  }
}

/**
 * Triggers token refresh by broadcasting to all clients.

 * includeUncontrolled: true ensures the message reaches tabs that loaded before
 * the Service Worker took control (e.g. first load, hard refresh).
 * type: 'window' filters out non-page clients (shared workers, etc.).

 * Each tab's TokenRefreshManager looks up refreshCallbacks.get(sessionId) and
 * either executes the refresh (if it owns that session) or silently ignores it.
 */
async function triggerRefresh(sessionId) {
  const tokenData = userTokens.get(sessionId);
  if (!tokenData) return;

  const clients = await self.clients.matchAll({
    includeUncontrolled: true,
    type: 'window',
  });

  clients.forEach((client) => {
    client.postMessage({
      type: 'REFRESH_TOKEN_REQUEST',
      payload: { sessionId },
    });
  });
}

/**
 * Stops token refresh monitoring for a session
 * Cleans up timers and stored token data
 */
function stopTokenRefresh(sessionId) {
  const timer = refreshTimers.get(sessionId);
  if (timer) {
    clearTimeout(timer);
    refreshTimers.delete(sessionId);
  }
  userTokens.delete(sessionId);
}
